/**
 * loadingButton.ts — a button that shows a "Loading" state while an async action
 * runs. Mirrors the intro button's wipe (a two-line label clipped to one, sliding
 * up) + palette transition, but in reverse: enabled → disabled. Used for the
 * Add-to-bag buttons (review screen + recent-creations cards) so the snapshot
 * upload wait is visible. See the .cfg-load-* CSS in sections/configurator.liquid.
 */

const el = (tag: string, cls?: string) => { const e = document.createElement(tag); if (cls) e.className = cls; return e; };

const DISABLED_PALETTE = ['bg-detail/30', 'text-foreground'];

/**
 * Build a button whose click runs `action()` while showing "Loading" + disabled,
 * then restores. `layout` = sizing/typography classes; `palette` = the enabled
 * colour classes (swapped out for the disabled palette while loading).
 */
export function makeLoadingButton(
  label: string,
  layout: string,
  palette: string,
  action: () => void | Promise<void>,
  loadingLabel = 'Loading',
  iconHtml?: string, // optional static icon (e.g. a heart) rendered after the wiping label
  opts: {
    disabledPalette?: string[]; // classes applied while loading (default: greyed fill)
    labelClass?: string;        // extra classes on the label wrapper (e.g. responsive hiding)
    iconFirst?: boolean;        // render the icon BEFORE the label (default: after)
  } = {},
): HTMLButtonElement {
  // btn--animate gives the usual hover wipe-up (on the label-inner line); the cfg-load
  // swap slides the whole stack to the "Loading" line while busy. The two coexist:
  // the wipe moves the inner text within line 1, the loading swap moves the stack.
  const btn = el('button', `cfg-load-btn btn--animate ${layout} ${palette}`) as HTMLButtonElement;
  btn.type = 'button';
  const labelEl = el('span', `btn__label ${opts.labelClass ?? ''}`.trim());
  const swap = el('span', 'cfg-load-swap');
  const l1 = el('span', 'btn__label-inner'); l1.textContent = label;
  const l2 = el('span'); l2.textContent = loadingLabel;
  swap.append(l1, l2);
  labelEl.appendChild(swap);
  let icon: HTMLElement | null = null;
  if (iconHtml) {
    icon = el('span', 'inline-flex items-center shrink-0');
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = iconHtml;
  }
  if (icon && opts.iconFirst) btn.appendChild(icon);
  btn.appendChild(labelEl);
  if (icon && !opts.iconFirst) btn.appendChild(icon);

  const paletteCls = palette.split(/\s+/).filter(Boolean);
  const disabledCls = opts.disabledPalette ?? DISABLED_PALETTE;
  let loading = false;
  const setLoading = (on: boolean): void => {
    if (on) {
      btn.style.width = btn.offsetWidth + 'px'; // lock so the label swap doesn't resize the button
      btn.disabled = true;
      btn.classList.add('is-loading');
      if (paletteCls.length) btn.classList.remove(...paletteCls);
      btn.classList.add(...disabledCls);
    } else {
      btn.disabled = false;
      btn.classList.remove('is-loading', ...disabledCls);
      if (paletteCls.length) btn.classList.add(...paletteCls);
      btn.style.width = '';
    }
  };

  btn.addEventListener('click', async () => {
    if (loading) return;
    loading = true;
    setLoading(true);
    try {
      await action();
    } finally {
      loading = false;
      setLoading(false);
    }
  });

  return btn;
}
